import type { SearchResult } from "@/schema/search"
import { MenuOption } from "@lexical/react/LexicalTypeaheadMenuPlugin"

export type MentionRecordType =
  | "Household"
  | "Client"
  | "PastClient"
  | "Prospect"
  | "Business"
  | "User"
  | "Team"
  | "Person"
  | "Contact"
  | "Opportunity"
  | "SMSMessage"
  | "Trust"
  | "CustodianAccount"
  | "FinancialAccount"
  | "InsurancePolicy"

export interface MentionableRecordMetadata {
  id: string
  type: string
  label: string
}

export interface MentionRecord {
  id: string
  label: string
  recordType: MentionRecordType
  mentionableRecord?: MentionableRecordMetadata
}

type MentionSearchResult = SearchResult | { type: "team" | "user"; id: string; display_label: string }

const RECORD_TYPE_MAP = {
  household: "Household",
  client: "Client",
  past_client: "PastClient",
  person: "Person",
  contact: "Contact",
  prospect: "Prospect",
  opportunity: "Opportunity",
  sms_message: "SMSMessage",
  business: "Business",
  user: "User",
  team: "Team",
  trust: "Trust",
  custodian_account: "CustodianAccount",
  financial_account: "FinancialAccount",
  insurance_policy: "InsurancePolicy",
} as const satisfies Record<MentionSearchResult["type"], MentionRecordType>

export class MentionOption extends MenuOption {
  searchResult: MentionSearchResult
  recordType: MentionRecordType

  constructor(searchResult: MentionSearchResult) {
    super(`${searchResult.type}-${searchResult.id}`)
    this.searchResult = searchResult
    this.recordType = this.mapRecordType(searchResult.type)
    this.setRefElement(null)
  }

  private mapRecordType(type: MentionSearchResult["type"]): MentionRecordType {
    return RECORD_TYPE_MAP[type]
  }

  get label(): string {
    return this.searchResult.display_label
  }

  get recordId(): string {
    return this.searchResult.id
  }
}

export interface MentionDropdownPluginProps {
  mentionableTypes: SearchResult["type"][]
  scopeUsersToBook?: boolean
  bookId?: string
  teamOptions?: MentionOption[]
  /** Replaces global search with a conversation-scoped list. */
  options?: MentionOption[]
  /** When set, household-linked custodian/manual/insurance accounts are offered as mentions. */
  householdId?: string | null
  onMentionAdded?: (mention: MentionRecord) => void
  onMentionRemoved?: (mention: MentionRecord) => void
  onOpenChange?: (isOpen: boolean) => void
}
